import React, { useState, useEffect } from 'react';
import { Car, Trash2, ChevronLeft, Clock, CheckCircle, Calendar, Plus, X, RefreshCw, Euro } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Konstanten
const TIME_SLOTS_ADMIN = Array.from({ length: 96 }, (_, i) => {
  const hours = Math.floor(i / 4);
  const minutes = (i % 4) * 15;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
});

const WEEKDAYS = [
  { value: 0, label: 'Mo', fullLabel: 'Montag' },
  { value: 1, label: 'Di', fullLabel: 'Dienstag' },
  { value: 2, label: 'Mi', fullLabel: 'Mittwoch' },
  { value: 3, label: 'Do', fullLabel: 'Donnerstag' },
  { value: 4, label: 'Fr', fullLabel: 'Freitag' },
  { value: 5, label: 'Sa', fullLabel: 'Samstag' },
  { value: 6, label: 'So', fullLabel: 'Sonntag' }
];

const RIDE_TYPES = [
  { value: 'tierarzt', label: 'Tierarztfahrt' },
  { value: 'tierklinik', label: 'Tierklinikfahrt' },
  { value: 'sonstiges', label: 'Sonstige Fahrt' }
];

const AdminCalendar = ({ storedPassword, onBack }) => {
  // State für Fahrten
  const [adminRides, setAdminRides] = useState([]);
  const [selectedRideIds, setSelectedRideIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newRide, setNewRide] = useState({
    date: new Date().toISOString().split('T')[0],
    timeSlots: [],
    customerName: '',
    customerPhone: '',
    petName: '',
    petType: 'Hund',
    rideType: 'tierarzt',
    price: '',
    paymentStatus: 'offen',
    notes: '',
    isRecurring: false,
    recurringWeeks: 4,
    recurringWeekdays: []
  });

  // State für Blockierungen
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlockedDate, setNewBlockedDate] = useState({
    date: '',
    slots: [],
    reason: '',
    allDay: true
  });

  // Daten laden
  useEffect(() => {
    loadAdminRides();
    loadBlockedDates();
  }, []);

  const loadAdminRides = async () => {
    try {
      const response = await axios.get(`${API}/bookings/admin/rides?password=${encodeURIComponent(storedPassword())}`);
      setAdminRides(response.data || []);
    } catch (error) {
      console.error('Error loading admin rides:', error);
    }
  };

  const loadBlockedDates = async () => {
    try {
      const response = await axios.get(`${API}/bookings/admin/blocked-dates?password=${encodeURIComponent(storedPassword())}`);
      setBlockedDates(response.data || []);
    } catch (error) {
      console.error('Error loading blocked dates:', error);
    }
  };

  // Berechne alle Termine basierend auf Wochentagen
  const calculateRecurringDates = (startDate, weekdays, weeks) => {
    const dates = [];
    const start = new Date(startDate + 'T00:00:00');
    
    const jsWeekdays = weekdays.map(d => d === 6 ? 0 : d + 1);
    
    for (let week = 0; week < weeks; week++) {
      for (const jsWeekday of jsWeekdays.sort((a, b) => a - b)) {
        const startDay = start.getDay();
        const startMondayOffset = startDay === 0 ? -6 : 1 - startDay;
        const weekStart = new Date(start);
        weekStart.setDate(start.getDate() + startMondayOffset + (week * 7));
        
        const targetDayOffset = jsWeekday === 0 ? 6 : jsWeekday - 1;
        const targetDate = new Date(weekStart);
        targetDate.setDate(weekStart.getDate() + targetDayOffset);
        
        if (targetDate >= start) {
          dates.push(targetDate.toISOString().split('T')[0]);
        }
      }
    }
    
    return [...new Set(dates)].sort();
  };

  // Fahrt erstellen
  const handleCreateRide = async () => {
    if (!newRide.date || newRide.timeSlots.length === 0 || !newRide.customerName || !newRide.price) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    setIsLoading(true);

    try {
      let datesToCreate = [newRide.date];
      
      if (newRide.isRecurring && newRide.recurringWeekdays.length > 0) {
        datesToCreate = calculateRecurringDates(
          newRide.date,
          newRide.recurringWeekdays,
          newRide.recurringWeeks
        );
      }

      const response = await axios.post(
        `${API}/bookings/admin/rides?password=${encodeURIComponent(storedPassword())}`,
        {
          ...newRide,
          dates: datesToCreate,
          price: parseFloat(newRide.price)
        }
      );

      if (response.data.success) {
        const count = response.data.rides?.length || 1;
        toast.success(`${count} Fahrt(en) erstellt`);
        loadAdminRides();
        loadBlockedDates();
        
        // Form zurücksetzen
        setNewRide({
          date: new Date().toISOString().split('T')[0],
          timeSlots: [],
          customerName: '',
          customerPhone: '',
          petName: '',
          petType: 'Hund',
          rideType: 'tierarzt',
          price: '',
          paymentStatus: 'offen',
          notes: '',
          isRecurring: false,
          recurringWeeks: 4,
          recurringWeekdays: []
        });
      }
    } catch (error) {
      toast.error('Fehler beim Erstellen der Fahrt');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fahrt löschen (einzeln)
  const handleDeleteRide = async (rideId) => {
    if (!window.confirm('Fahrt wirklich löschen? Der Buchhaltungseintrag wird ebenfalls gelöscht.')) return;
    
    try {
      await axios.delete(`${API}/bookings/admin/rides/${rideId}?password=${encodeURIComponent(storedPassword())}`);
      toast.success('Fahrt gelöscht');
      setSelectedRideIds(prev => prev.filter(id => id !== rideId));
      loadAdminRides();
      loadBlockedDates();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Mehrere Fahrten löschen
  const handleDeleteSelectedRides = async () => {
    if (selectedRideIds.length === 0) {
      toast.error('Keine Fahrten ausgewählt');
      return;
    }
    
    if (!window.confirm(`${selectedRideIds.length} Fahrt(en) wirklich löschen? Die zugehörigen Buchhaltungseinträge werden ebenfalls gelöscht.`)) return;
    
    setIsLoading(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const rideId of selectedRideIds) {
      try {
        await axios.delete(`${API}/bookings/admin/rides/${rideId}?password=${encodeURIComponent(storedPassword())}`);
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Fehler beim Löschen von ${rideId}:`, error);
      }
    }
    
    setIsLoading(false);
    setSelectedRideIds([]);
    
    if (successCount > 0) {
      toast.success(`${successCount} Fahrt(en) gelöscht` + (errorCount > 0 ? `, ${errorCount} fehlgeschlagen` : ''));
    } else {
      toast.error('Keine Fahrten konnten gelöscht werden');
    }
    
    loadAdminRides();
    loadBlockedDates();
  };

  // Fahrt-Auswahl toggle
  const toggleRideSelection = (rideId) => {
    setSelectedRideIds(prev => 
      prev.includes(rideId) 
        ? prev.filter(id => id !== rideId)
        : [...prev, rideId]
    );
  };

  // Alle Fahrten auswählen/abwählen
  const toggleAllRides = () => {
    if (selectedRideIds.length === adminRides.length) {
      setSelectedRideIds([]);
    } else {
      setSelectedRideIds(adminRides.map(r => r.id));
    }
  };

  // Zahlungsstatus ändern (mit optionaler Preis-Anpassung)
  const handleUpdateRidePayment = async (rideId, status, customPrice = null) => {
    try {
      let url = `${API}/bookings/admin/rides/${rideId}?password=${encodeURIComponent(storedPassword())}&paymentStatus=${status}`;
      if (customPrice !== null && customPrice !== undefined && customPrice !== '') {
        url += `&price=${encodeURIComponent(customPrice)}`;
      }
      await axios.put(url);
      toast.success(status === 'bezahlt' ? 'Zahlung bestätigt - Rechnung wird per E-Mail versandt' : 'Zahlungsstatus aktualisiert');
      loadAdminRides();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Dialog state für Zahlungsbestätigung mit editierbarem Preis
  const [paymentConfirmDialog, setPaymentConfirmDialog] = useState({ open: false, ride: null, editedPrice: '' });

  const openPaymentConfirm = (ride) => {
    setPaymentConfirmDialog({
      open: true,
      ride,
      editedPrice: (ride.price ?? 0).toString()
    });
  };

  const submitPaymentConfirm = async () => {
    const { ride, editedPrice } = paymentConfirmDialog;
    if (!ride) return;
    const priceNum = parseFloat(editedPrice.toString().replace(',', '.'));
    if (isNaN(priceNum) || priceNum < 0) {
      toast.error('Bitte einen gültigen Betrag eingeben');
      return;
    }
    setPaymentConfirmDialog({ open: false, ride: null, editedPrice: '' });
    await handleUpdateRidePayment(ride.id, 'bezahlt', priceNum);
  };

  // Datum blockieren
  const handleBlockDate = async () => {
    if (!newBlockedDate.date) {
      toast.error('Bitte ein Datum auswählen');
      return;
    }

    try {
      await axios.post(
        `${API}/bookings/admin/blocked-dates?password=${encodeURIComponent(storedPassword())}`,
        {
          date: newBlockedDate.date,
          slots: newBlockedDate.allDay ? [] : newBlockedDate.slots,
          reason: newBlockedDate.reason
        }
      );
      toast.success('Termin blockiert');
      loadBlockedDates();
      setNewBlockedDate({ date: '', slots: [], reason: '', allDay: true });
    } catch (error) {
      toast.error('Fehler beim Blockieren');
    }
  };

  // Blockierung entfernen
  const handleUnblockDate = async (date) => {
    if (!window.confirm('Blockierung für diesen Tag entfernen?')) return;
    
    try {
      await axios.delete(
        `${API}/bookings/admin/blocked-dates/${date}?password=${encodeURIComponent(storedPassword())}`
      );
      toast.success('Blockierung entfernt');
      loadBlockedDates();
    } catch (error) {
      toast.error('Fehler beim Entfernen');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ChevronLeft className="h-4 w-4 mr-2" /> Zurück
        </Button>
        
        <div className="grid lg:grid-cols-2 gap-6">
          {/* LINKE SPALTE: Fahrt erstellen */}
          <Card>
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center">
                <Car className="h-5 w-5 mr-2" /> Fahrt eintragen
              </CardTitle>
              <CardDescription className="text-blue-100">
                Erstellt Buchung, blockiert Zeitslot & Buchhaltungseintrag
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {/* Datum */}
              <div>
                <Label>Datum *</Label>
                <Input
                  type="date"
                  value={newRide.date}
                  onChange={(e) => setNewRide(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>

              {/* Zeitslots - Multi-Select */}
              <div>
                <Label>Zeitslots * <span className="text-xs text-gray-500">(Mehrfachauswahl möglich)</span></Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    type="time"
                    placeholder="Schnellauswahl"
                    className="w-32"
                    onChange={(e) => {
                      const slot = e.target.value;
                      if (slot && !newRide.timeSlots.includes(slot)) {
                        setNewRide(prev => ({ ...prev, timeSlots: [...prev.timeSlots, slot].sort() }));
                      }
                      e.target.value = '';
                    }}
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setNewRide(prev => ({ ...prev, timeSlots: [] }))}
                  >
                    Alle abwählen
                  </Button>
                </div>
                
                {/* Ausgewählte Slots anzeigen */}
                {newRide.timeSlots.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2 p-2 bg-blue-50 rounded-lg">
                    {newRide.timeSlots.sort().map(slot => (
                      <span
                        key={slot}
                        onClick={() => setNewRide(prev => ({
                          ...prev,
                          timeSlots: prev.timeSlots.filter(s => s !== slot)
                        }))}
                        className="px-2 py-1 bg-blue-600 text-white text-xs rounded cursor-pointer hover:bg-blue-700"
                      >
                        {slot} ✕
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Schnellauswahl-Buttons */}
                <div className="grid grid-cols-6 gap-1 max-h-32 overflow-y-auto p-1 border rounded">
                  {['08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', 
                    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
                    '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30'].map(slot => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => {
                        setNewRide(prev => ({
                          ...prev,
                          timeSlots: prev.timeSlots.includes(slot)
                            ? prev.timeSlots.filter(s => s !== slot)
                            : [...prev.timeSlots, slot].sort()
                        }));
                      }}
                      className={`text-xs p-1 rounded transition-colors ${
                        newRide.timeSlots.includes(slot)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              {/* Wiederkehrend */}
              <div className="border-t pt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newRide.isRecurring}
                    onChange={(e) => setNewRide(prev => ({ ...prev, isRecurring: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <span className="font-medium">Wiederkehrende Fahrt</span>
                </label>
                
                {newRide.isRecurring && (
                  <div className="mt-3 p-3 bg-purple-50 rounded-lg space-y-3">
                    <div>
                      <Label>Wochentage auswählen</Label>
                      <div className="flex gap-1 mt-1">
                        {WEEKDAYS.map(day => (
                          <button
                            key={day.value}
                            type="button"
                            onClick={() => {
                              setNewRide(prev => ({
                                ...prev,
                                recurringWeekdays: prev.recurringWeekdays.includes(day.value)
                                  ? prev.recurringWeekdays.filter(d => d !== day.value)
                                  : [...prev.recurringWeekdays, day.value]
                              }));
                            }}
                            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                              newRide.recurringWeekdays.includes(day.value)
                                ? 'bg-purple-600 text-white'
                                : 'bg-white border hover:bg-purple-50'
                            }`}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                      {newRide.recurringWeekdays.length > 0 && (
                        <p className="text-xs text-purple-600 mt-1">
                          Jeden {newRide.recurringWeekdays.map(d => WEEKDAYS.find(w => w.value === d)?.fullLabel).join(', ')}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Anzahl Wochen</Label>
                      <select
                        value={newRide.recurringWeeks}
                        onChange={(e) => setNewRide(prev => ({ ...prev, recurringWeeks: parseInt(e.target.value) }))}
                        className="w-full p-2 border rounded"
                      >
                        {[2, 4, 6, 8, 12].map(w => (
                          <option key={w} value={w}>{w} Wochen</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Kundendaten */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Kundenname *</Label>
                  <Input
                    value={newRide.customerName}
                    onChange={(e) => setNewRide(prev => ({ ...prev, customerName: e.target.value }))}
                    placeholder="Max Mustermann"
                  />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input
                    value={newRide.customerPhone}
                    onChange={(e) => setNewRide(prev => ({ ...prev, customerPhone: e.target.value }))}
                    placeholder="0157..."
                  />
                </div>
              </div>

              {/* Tierdaten */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tiername</Label>
                  <Input
                    value={newRide.petName}
                    onChange={(e) => setNewRide(prev => ({ ...prev, petName: e.target.value }))}
                    placeholder="Bello"
                  />
                </div>
                <div>
                  <Label>Tierart</Label>
                  <select
                    value={newRide.petType}
                    onChange={(e) => setNewRide(prev => ({ ...prev, petType: e.target.value }))}
                    className="w-full p-2 border rounded"
                  >
                    <option value="Hund">Hund</option>
                    <option value="Katze">Katze</option>
                    <option value="Kleintier">Kleintier</option>
                    <option value="Sonstiges">Sonstiges</option>
                  </select>
                </div>
              </div>

              {/* Fahrttyp & Preis */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fahrttyp</Label>
                  <select
                    value={newRide.rideType}
                    onChange={(e) => setNewRide(prev => ({ ...prev, rideType: e.target.value }))}
                    className="w-full p-2 border rounded"
                  >
                    {RIDE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Preis (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newRide.price}
                    onChange={(e) => setNewRide(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="25.00"
                  />
                </div>
              </div>

              {/* Zahlungsstatus */}
              <div>
                <Label>Zahlungsstatus</Label>
                <div className="flex gap-2">
                  {['offen', 'bezahlt'].map(status => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => setNewRide(prev => ({ ...prev, paymentStatus: status }))}
                      className={`px-4 py-2 rounded ${
                        newRide.paymentStatus === status
                          ? status === 'bezahlt' ? 'bg-green-600 text-white' : 'bg-yellow-500 text-white'
                          : 'bg-gray-100'
                      }`}
                    >
                      {status === 'bezahlt' ? 'Bezahlt' : 'Offen'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notizen */}
              <div>
                <Label>Notizen</Label>
                <textarea
                  value={newRide.notes}
                  onChange={(e) => setNewRide(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full p-2 border rounded h-20"
                  placeholder="Zusätzliche Informationen..."
                />
              </div>

              {/* Submit Button */}
              <Button 
                onClick={handleCreateRide} 
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {isLoading ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Erstellen...</>
                ) : (
                  <><Plus className="h-4 w-4 mr-2" /> Fahrt(en) erstellen</>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* RECHTE SPALTE: Fahrten & Blockierungen */}
          <div className="space-y-6">
            {/* Fahrten-Liste */}
            <Card>
              <CardHeader className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-t-lg">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center">
                    <Car className="h-5 w-5 mr-2" /> Geplante Fahrten ({adminRides.length})
                  </span>
                  {adminRides.length > 0 && selectedRideIds.length > 0 && (
                    <span className="text-sm font-normal opacity-90">
                      {selectedRideIds.length} ausgewählt
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {/* Aktionsleiste für Mehrfachauswahl */}
                {adminRides.length > 0 && (
                  <div className="flex items-center justify-between mb-3 pb-3 border-b">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={selectedRideIds.length === adminRides.length && adminRides.length > 0}
                        onChange={toggleAllRides}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-gray-600">Alle auswählen</span>
                    </label>
                    {selectedRideIds.length > 0 && (
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={handleDeleteSelectedRides}
                        disabled={isLoading}
                        className="bg-red-500 hover:bg-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        {isLoading ? 'Lösche...' : `${selectedRideIds.length} löschen`}
                      </Button>
                    )}
                  </div>
                )}
                
                <div className="max-h-80 overflow-y-auto">
                  {adminRides.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Car className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                      <p>Keine Fahrten eingetragen</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {adminRides.map(ride => (
                        <div 
                          key={ride.id} 
                          className={`p-3 rounded-lg border transition-colors ${
                            selectedRideIds.includes(ride.id) 
                              ? 'bg-blue-50 border-blue-300' 
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Checkbox */}
                            <input
                              type="checkbox"
                              checked={selectedRideIds.includes(ride.id)}
                              onChange={() => toggleRideSelection(ride.id)}
                              className="w-4 h-4 mt-1 rounded border-gray-300 cursor-pointer"
                            />
                            
                            {/* Fahrt-Details */}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold truncate">{ride.customerName}</p>
                              <p className="text-sm text-gray-600">
                                {new Date(ride.date + 'T00:00:00').toLocaleDateString('de-DE', {
                                  weekday: 'short',
                                  day: '2-digit',
                                  month: '2-digit'
                                })} • {ride.timeSlots && ride.timeSlots.length > 1 
                                  ? `${ride.timeSlots[0]} - ${ride.timeSlots[ride.timeSlots.length - 1]} Uhr` 
                                  : `${ride.timeSlots?.[0] || ride.timeSlot} Uhr`}
                              </p>
                              <p className="text-sm text-gray-500 truncate">
                                {ride.petName && `${ride.petName} • `}
                                {ride.rideType === 'tierarzt' ? 'Tierarzt' : ride.rideType === 'tierklinik' ? 'Tierklinik' : 'Sonstige'}
                              </p>
                            </div>
                            
                            {/* Preis & Aktionen */}
                            <div className="text-right flex-shrink-0">
                              <p className="font-bold text-green-600">{ride.price?.toFixed(2)} €</p>
                              <span className={`text-xs px-2 py-1 rounded ${
                                ride.paymentStatus === 'bezahlt' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {ride.paymentStatus}
                              </span>
                              <div className="flex gap-1 mt-2 justify-end">
                                {ride.paymentStatus === 'offen' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openPaymentConfirm(ride)}
                                    data-testid={`confirm-payment-btn-${ride.id}`}
                                    title="Zahlungseingang bestätigen"
                                  >
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteRide(ride.id)}>
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Termin blockieren */}
            <Card>
              <CardHeader className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-t-lg">
                <CardTitle className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2" /> Termin blockieren
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label>Datum</Label>
                  <Input
                    type="date"
                    value={newBlockedDate.date}
                    onChange={(e) => setNewBlockedDate(prev => ({ ...prev, date: e.target.value }))}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newBlockedDate.allDay}
                    onChange={(e) => setNewBlockedDate(prev => ({ ...prev, allDay: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <span>Ganzen Tag blockieren</span>
                </div>

                {!newBlockedDate.allDay && (
                  <div>
                    <Label>Zeitslots auswählen</Label>
                    <div className="grid grid-cols-4 gap-1 mt-2 max-h-32 overflow-y-auto">
                      {TIME_SLOTS_ADMIN.filter(s => s >= '08:00' && s <= '20:00').map(slot => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => {
                            setNewBlockedDate(prev => ({
                              ...prev,
                              slots: prev.slots.includes(slot)
                                ? prev.slots.filter(s => s !== slot)
                                : [...prev.slots, slot]
                            }));
                          }}
                          className={`text-xs p-1 rounded ${
                            newBlockedDate.slots.includes(slot)
                              ? 'bg-red-500 text-white'
                              : 'bg-gray-100'
                          }`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label>Grund (intern)</Label>
                  <Input
                    value={newBlockedDate.reason}
                    onChange={(e) => setNewBlockedDate(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="z.B. Urlaub, Fahrzeug in Werkstatt..."
                  />
                </div>

                <Button onClick={handleBlockDate} className="w-full bg-red-500 hover:bg-red-600">
                  <X className="h-4 w-4 mr-2" /> Termin blockieren
                </Button>

                {/* Blockierte Termine anzeigen */}
                {blockedDates.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <Label className="mb-2 block">Blockierte Termine</Label>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {blockedDates.map(blocked => (
                        <div key={blocked.date} className="flex items-center justify-between p-2 bg-red-50 rounded">
                          <div>
                            <span className="font-medium">
                              {new Date(blocked.date + 'T00:00:00').toLocaleDateString('de-DE', {
                                weekday: 'short',
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </span>
                            {blocked.allDay ? (
                              <span className="ml-2 text-xs text-red-600">(Ganzer Tag)</span>
                            ) : (
                              <span className="ml-2 text-xs text-gray-500">
                                {blocked.slots?.length || 0} Slots
                              </span>
                            )}
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => handleUnblockDate(blocked.date)}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Dialog: Zahlungseingang bestätigen + Preis bearbeiten */}
      <Dialog
        open={paymentConfirmDialog.open}
        onOpenChange={(open) => !open && setPaymentConfirmDialog({ open: false, ride: null, editedPrice: '' })}
      >
        <DialogContent data-testid="payment-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Zahlungseingang bestätigen</DialogTitle>
            <DialogDescription>
              Bitte den tatsächlich erhaltenen Rechnungsbetrag prüfen und ggf. anpassen.
              Nach Bestätigung wird automatisch eine Rechnung per E-Mail an den Kunden gesendet.
            </DialogDescription>
          </DialogHeader>

          {paymentConfirmDialog.ride && (
            <div className="space-y-4 py-2">
              <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                <div><span className="text-gray-500">Kunde:</span> <strong>{paymentConfirmDialog.ride.customerName}</strong></div>
                <div><span className="text-gray-500">Datum:</span> {paymentConfirmDialog.ride.date}</div>
                {paymentConfirmDialog.ride.customerEmail && (
                  <div><span className="text-gray-500">E-Mail:</span> {paymentConfirmDialog.ride.customerEmail}</div>
                )}
                {!paymentConfirmDialog.ride.customerEmail && (
                  <div className="text-orange-600 text-xs mt-1">⚠ Keine E-Mail hinterlegt - es wird keine Rechnung versendet.</div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-price" className="flex items-center gap-2">
                  <Euro className="h-4 w-4 text-blue-600" />
                  Rechnungsbetrag (EUR)
                </Label>
                <Input
                  id="confirm-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentConfirmDialog.editedPrice}
                  onChange={(e) => setPaymentConfirmDialog(prev => ({ ...prev, editedPrice: e.target.value }))}
                  data-testid="confirm-payment-price-input"
                  autoFocus
                />
                <p className="text-xs text-gray-500">
                  Dieser Betrag erscheint auf der Rechnung und im Buchhaltungseintrag.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setPaymentConfirmDialog({ open: false, ride: null, editedPrice: '' })}
              data-testid="confirm-payment-cancel-btn"
            >
              Abbrechen
            </Button>
            <Button
              onClick={submitPaymentConfirm}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-payment-submit-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Zahlung bestätigen & Rechnung senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCalendar;
